import { useState, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, ArrowLeft, RefreshCw, Mail } from 'lucide-react';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from "@hookform/resolvers/zod";
import { fetchUserAttributes } from 'aws-amplify/auth';

import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import AuthService from "@/lib/auth";
import biometricAuthService from "@/services/biometricAuthService";
import { isStaffRole, generateMfaOtp, sendMfaEmail, maskEmail } from "@/utils/mfaEmail";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface PendingStaffMfa {
  email: string;
  username: string;
  name: string;
  role: string | null;
  otp: string;
  expiresAt: number;
}

export default function LoginForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("");

  // MFA State for Staff Roles
  const [isMfaStep, setIsMfaStep] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [pendingStaff, setPendingStaff] = useState<PendingStaffMfa | null>(null);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    document.title = isMfaStep ? "Monitor Health - Two-Factor Authentication" : "Monitor Health - Login";

    const checkAuth = async () => {
      try {
        const currentUser = await AuthService.getCurrentUser();
        if (currentUser) {
          const userAttributes = (await fetchUserAttributes().catch(() => ({}))) as Record<string, string>;
          const email = userAttributes.email || currentUser.username;
          let profile = null;
          try {
            profile = await AuthService.getProfile(email);
          } catch {
            profile = null;
          }
          if (!profile && currentUser.username) {
            try {
              profile = await AuthService.getProfile(currentUser.username);
            } catch {
              profile = null;
            }
          }
          const role = profile?.role || profile?.staff_role || userAttributes['custom:role'] || userAttributes.role;
          const isStaff = isStaffRole(role);
          const mfaVerified = sessionStorage.getItem('mfa_verified') === 'true';

          // Patients bypass MFA; Staff require verified session
          if (!isStaff || mfaVerified) {
            navigate(from, { replace: true });
          }
        }
      } catch (error) {
        console.error("Auth check error:", error);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [navigate, from, isMfaStep]);

  // Countdown timer for resending OTP
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Countdown timer for MFA OTP expiration
  useEffect(() => {
    if (!pendingStaff?.expiresAt) {
      setOtpSecondsLeft(0);
      return;
    }

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((pendingStaff.expiresAt - Date.now()) / 1000));
      setOtpSecondsLeft(remaining);
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [pendingStaff?.expiresAt]);

  const formatOtpTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const onSubmit = async (data: LoginFormData) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setLoadingMessage("Signing in…");
    try {
      const { user, error, requiresEmailConfirmation } = await AuthService.signIn(data.email, data.password);

      // Cognito account exists but email not yet confirmed — redirect to verification page.
      if (requiresEmailConfirmation) {
        toast.info("Please verify your email before logging in. Check your inbox for the confirmation code.");
        navigate('/verify-email', { state: { email: data.email } });
        return;
      }

      if (error) {
        throw new Error(error.message || 'Failed to sign in');
      }

      if (user) {
        // Retrieve profile and Cognito user attributes to accurately identify role
        setLoadingMessage("Loading your profile…");
        let userAttributes: Record<string, string> = {};
        try {
          userAttributes = (await fetchUserAttributes().catch(() => ({}))) as Record<string, string>;
        } catch {
          userAttributes = {};
        }

        let profile = null;
        try {
          profile = await AuthService.getProfile(data.email);
        } catch {
          profile = null;
        }

        if (!profile && user.username) {
          try {
            profile = await AuthService.getProfile(user.username);
          } catch {
            profile = null;
          }
        }

        const effectiveRole =
          profile?.role ||
          profile?.staff_role ||
          userAttributes['custom:role'] ||
          userAttributes.role ||
          user.role ||
          null;

        const isStaff = isStaffRole(effectiveRole);

        // PATIENTS: Kept completely as-is, no MFA required
        if (!isStaff) {
          sessionStorage.setItem('mfa_verified', 'true');
          toast.success("Login successful!");
          navigate(from, { replace: true });
          return;
        }

        // ALL STAFF ROLES (Doctor, Nurse, Admin, etc.): Require Email OTP MFA
        const staffName =
          profile?.name ||
          profile?.full_name ||
          userAttributes.name ||
          user.name ||
          data.email.split('@')[0];
        const generatedOtp = generateMfaOtp();
        const expiresAt = Date.now() + 10 * 60 * 1000; // 10 minutes validity

        // Also output OTP to console in dev mode so staff/testers are never locked out
        if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
          console.log(`%c[MFA DEV OTP CODE]: ${generatedOtp}`, 'color: #10b981; font-weight: bold; font-size: 16px;');
        }

        setLoadingMessage("Sending security code to your email…");
        const sendResult = await sendMfaEmail({
          to: data.email,
          name: staffName,
          otpCode: generatedOtp,
          expirationMinutes: 10,
        });

        if (!sendResult.success) {
          toast.error("Could not send MFA security code", {
            description: sendResult.error || "Please try again in a few moments.",
          });
          return;
        }

        setPendingStaff({
          email: data.email,
          username: user.username || user.id,
          name: staffName,
          role: effectiveRole,
          otp: generatedOtp,
          expiresAt,
        });
        setIsMfaStep(true);
        setOtpCode("");
        setResendCooldown(30);
        toast.success(`Verification code sent to ${maskEmail(data.email)}`);
      }
    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(error.message || "Login failed. Please check your credentials and try again.");
    } finally {
      setIsSubmitting(false);
      setLoadingMessage("");
    }
  };

  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!pendingStaff || isVerifyingOtp) return;

    const trimmed = otpCode.trim();
    if (trimmed.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    if (Date.now() > pendingStaff.expiresAt) {
      toast.error("Verification code has expired", {
        description: "Please request a new code using the Resend button below.",
      });
      return;
    }

    if (trimmed !== pendingStaff.otp) {
      toast.error("Invalid verification code", {
        description: "The code you entered is incorrect. Please check your email.",
      });
      return;
    }

    setIsVerifyingOtp(true);
    setLoadingMessage("Verifying code…");
    try {
      setLoadingMessage("Completing secure login…");
      // Complete backend token exchange for staff
      await biometricAuthService.login(pendingStaff.username, true);
      // Mark MFA verified for this session
      sessionStorage.setItem('mfa_verified', 'true');
      setLoadingMessage("Welcome back!");
      toast.success(`Verification successful! Welcome back, ${pendingStaff.name}`);
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error("MFA completion error:", err);
      toast.error("Failed to complete login", {
        description: err.message || "Please try again.",
      });
    } finally {
      setIsVerifyingOtp(false);
      setLoadingMessage("");
    }
  };

  const handleResendCode = async () => {
    if (!pendingStaff || resendCooldown > 0 || isResending) return;
    setIsResending(true);
    try {
      const newOtp = generateMfaOtp();
      const expiresAt = Date.now() + 10 * 60 * 1000;

      const sendResult = await sendMfaEmail({
        to: pendingStaff.email,
        name: pendingStaff.name,
        otpCode: newOtp,
        expirationMinutes: 10,
      });

      if (!sendResult.success) {
        toast.error("Failed to resend code", {
          description: sendResult.error || "Please try again.",
        });
        return;
      }

      setPendingStaff((prev) => (prev ? { ...prev, otp: newOtp, expiresAt } : null));
      setResendCooldown(30);
      setOtpCode("");
      toast.success(`New verification code sent to ${maskEmail(pendingStaff.email)}`);
    } catch {
      toast.error("Failed to resend code. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToLogin = async () => {
    setIsMfaStep(false);
    setPendingStaff(null);
    setOtpCode("");
    setOtpSecondsLeft(0);
    sessionStorage.removeItem('mfa_verified');
    await AuthService.signOut();
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#f7f8fa]">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center shadow-md">
            <Loader2 className="h-8 w-8 animate-spin text-[#1a7a4a]" />
          </div>
        </div>
        <div className="text-center space-y-1">
          <p className="text-sm font-semibold text-slate-700">Checking your session…</p>
          <p className="text-xs text-slate-400">Please wait a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f7f8fa] min-h-screen flex items-center justify-center font-sans py-10 px-4">
      <div className="w-full max-w-md mx-auto">
        <Card className="w-full p-6 shadow-lg border border-slate-200">
          
          {/* ────────────────── MFA STEP ────────────────── */}
          {isMfaStep && pendingStaff ? (
            <div>
              <CardHeader className="text-center space-y-2 pb-4">
                <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 shadow-sm mb-2">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                  Two-Factor Authentication
                </h1>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Enter the 6-digit security code sent to your staff email:
                </p>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-800 rounded-full text-xs font-semibold border border-emerald-200 mt-1">
                  <Mail className="w-3.5 h-3.5" />
                  <span>{maskEmail(pendingStaff.email)}</span>
                </div>
              </CardHeader>

              <CardContent className="space-y-5 pt-2">
                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider block text-center">
                      6-Digit Security Code
                    </label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      autoFocus
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtpCode(val);
                      }}
                      placeholder="••••••"
                      disabled={isVerifyingOtp}
                      className="text-center font-mono text-2xl tracking-[0.4em] h-14 font-bold border-2 border-emerald-200 focus-visible:border-emerald-600 focus-visible:ring-emerald-500 rounded-xl"
                    />
                    <p
                      className={`text-[11px] text-center transition-colors ${
                        otpSecondsLeft <= 0
                          ? "text-red-500 font-medium"
                          : otpSecondsLeft <= 60
                          ? "text-amber-600 font-medium"
                          : "text-slate-400"
                      }`}
                    >
                      {otpSecondsLeft > 0 ? (
                        <>
                          Expires in <span className="font-mono font-medium">{formatOtpTimer(otpSecondsLeft)}</span> &bull; Single-use security token
                        </>
                      ) : (
                        "Verification code expired • Please request a new code"
                      )}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={otpCode.length !== 6 || isVerifyingOtp || otpSecondsLeft <= 0}
                    className="w-full bg-[#1a7a4a] hover:bg-[#15633c] text-white rounded-2xl h-12 shadow-lg shadow-green-100 transition-all hover:scale-[1.01] font-semibold"
                  >
                    {isVerifyingOtp ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {loadingMessage || "Verifying…"}
                      </>
                    ) : otpSecondsLeft <= 0 ? (
                      'Code Expired'
                    ) : (
                      'Verify & Sign In'
                    )}
                  </Button>
                </form>

                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <span>Didn't get the code?</span>
                  {resendCooldown > 0 ? (
                    <span className="text-slate-400 font-medium">
                      Resend in {resendCooldown}s
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={isResending}
                      className="text-[#1a7a4a] hover:underline font-semibold flex items-center gap-1"
                    >
                      {isResending ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                      )}
                      Resend Code
                    </button>
                  )}
                </div>
              </CardContent>

              <CardFooter className="pt-2">
                <Button
                  variant="ghost"
                  onClick={handleBackToLogin}
                  className="w-full text-slate-500 hover:text-slate-800 flex items-center justify-center gap-2 text-xs"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to Sign In
                </Button>
              </CardFooter>
            </div>
          ) : (
            /* ────────────────── STANDARD LOGIN STEP ────────────────── */
            <div>
              <CardHeader className="text-center space-y-2">
                <h1 className="text-3xl font-extrabold text-[#1a7a4a] tracking-tight flex items-baseline justify-center">
                  Monitor Health <span className="font-light text-gray-400 ml-2" style={{ fontSize: "20px", letterSpacing: "0.02em" }}>AI</span>
                </h1>
                <p className="text-muted-foreground">
                  Welcome back, login to access your account
                </p>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter your email"
                              type="email"
                              autoComplete="email"
                              {...field}
                              disabled={isSubmitting}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel>Password</FormLabel>
                            <Link
                              to="/auth/forgot-password"
                              className="text-sm font-medium text-primary hover:underline"
                            >
                              Forgot password?
                            </Link>
                          </div>
                          <FormControl>
                            <Input
                              placeholder="••••••••"
                              type="password"
                              autoComplete="current-password"
                              {...field}
                              disabled={isSubmitting}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="submit"
                      className="w-full bg-[#1a7a4a] hover:bg-[#15633c] text-white rounded-2xl h-12 shadow-lg shadow-green-100 transition-all hover:scale-[1.02] font-semibold"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {loadingMessage || "Signing in…"}
                        </>
                      ) : 'Sign In'}
                    </Button>
                  </form>
                </Form>
              </CardContent>
              <CardFooter>
                <div className="text-center text-sm w-full">
                  Don't have an account?{" "}
                  <Link to="/auth/register" className="text-primary hover:underline">
                    Sign up
                  </Link>
                </div>
              </CardFooter>
            </div>
          )}

        </Card>
      </div>
    </div>
  );
}